import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import type { SettlementByBudgetResponse } from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { settlementApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { AppModal } from '../../components/ui/AppModal';
import { MoneyReceivedDetailModal } from '../../components/settlement/MoneyReceivedDetailModal';
import { HelperResubmitSettlementBanner } from '../../components/settlement/HelperResubmitSettlementBanner';
import {
  SettlementDetailTitleMenu,
  type SettlementDetailTitleMenuItem,
} from '../../components/settlement/SettlementDetailTitleMenu';
import { SettlementHistorySection } from '../../components/settlement/employer/SettlementHistorySection';
import { PreviousHelperBanner } from '../../components/pairing/PreviousHelperBanner';
import {
  formatEmployerMoneyAmount,
} from '../../utils/employerMoneyGivenSettlement';

type SettlementByBudgetLocationState = {
  from?: string;
};

const resolveSettlementByBudgetReturnPath = (
  locationState: unknown,
  fallbackPath: string,
): string => {
  if (!locationState || typeof locationState !== 'object' || !('from' in locationState)) {
    return fallbackPath;
  }
  const from = (locationState as SettlementByBudgetLocationState).from?.trim() ?? '';
  if (from.startsWith('/')) {
    return from;
  }
  return fallbackPath;
};

export const EmployerSettlementByBudgetDetailPage = () => {
  const { budgetId } = useParams<{ budgetId: string }>();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<SettlementByBudgetResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moneyGivenPickerOpen, setMoneyGivenPickerOpen] = useState(false);
  const [moneyGivenDetailModalOpen, setMoneyGivenDetailModalOpen] = useState(false);
  const [moneyReceivedDetailModalOpen, setMoneyReceivedDetailModalOpen] = useState(false);

  const isEmployer = user?.role === USER_ROLE.EMPLOYER;
  const isHelper = user?.role === USER_ROLE.HELPER;
  const isFromPreviousHelper = data?.isFromPreviousHelper === true;
  const readOnlyView = isHelper || isFromPreviousHelper;

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  const load = useCallback(async () => {
    if (!budgetId) {
      setError(t('settlement.error'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await settlementApi.getByBudget(budgetId, language);
      if (res.success && res.data) {
        if (res.data.activeSettlement?.status === 'submitted' || res.data.activeSettlement?.status === 'reviewed') {
          navigate(`/settlements/${res.data.activeSettlement.id}`, {
            replace: true,
            state: location.state,
          });
          return;
        }
        setData(res.data);
        return;
      }
      setError(res.error ?? t('settlement.error'));
    } catch {
      setError(t('settlement.error'));
    } finally {
      setLoading(false);
    }
  }, [budgetId, language, location.state, navigate, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleHeaderBack = () => {
    const fallbackPath = isHelper ? '/settlements/history?view=money-received' : '/settlements';
    navigateBack(navigate, resolveSettlementByBudgetReturnPath(location.state, fallbackPath));
  };

  const preview = data?.unsettledPreview;
  const subMoneyGivenRows = data?.budget.children ?? [];
  const hasSubMoneyGiven = subMoneyGivenRows.length > 0;
  const subMoneyGivenTotal = subMoneyGivenRows.reduce((sum, row) => sum + row.amount, 0);
  const mainMoneyGivenAmount = Math.max(0, (data?.budget.amount ?? 0) - subMoneyGivenTotal);
  const balanceClass =
    (preview?.balance ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';

  const handleOpenMoneyGivenEdit = useCallback(() => {
    if (!data) {
      return;
    }
    if (!hasSubMoneyGiven) {
      navigate(`/budgets/${data.budget.id}/edit`, { state: { from: currentPath } });
      return;
    }
    setMoneyGivenPickerOpen(true);
  }, [currentPath, data, hasSubMoneyGiven, navigate]);

  const handleAppendMoneyGiven = useCallback(() => {
    if (!data) {
      return;
    }
    navigate(`/budgets/new?parentBudgetId=${encodeURIComponent(data.budget.id)}`, {
      state: { from: currentPath },
    });
  }, [currentPath, data, navigate]);

  const titleMenuItems = useMemo((): SettlementDetailTitleMenuItem[] => {
    if (!data || !preview || readOnlyView) {
      return [];
    }
    return [
      {
        id: 'money-given-detail',
        label: t('settlement.moneyGivenDetailTitle'),
        onSelect: () => setMoneyGivenDetailModalOpen(true),
      },
      {
        id: 'edit-money-given',
        label: t('settlement.menuEditMoneyGiven'),
        onSelect: handleOpenMoneyGivenEdit,
      },
      {
        id: 'append-money-given',
        label: t('settlement.menuAppendMoneyGiven'),
        onSelect: handleAppendMoneyGiven,
      },
    ];
  }, [data, handleAppendMoneyGiven, handleOpenMoneyGivenEdit, preview, readOnlyView, t]);

  const showHelperResubmitBanner =
    isHelper && Boolean(data?.budget.id) && data?.activeSettlement === null;

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950">
      <header className="bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <button
                type="button"
                onClick={handleHeaderBack}
                aria-label={t('task.action.back')}
                className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4L6 10l6 6" />
                </svg>
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('settlement.detailTitle')}
                </h1>
                {data ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('settlement.period', {
                      start: data.budget.startDate,
                      end: data.budget.endDate,
                    })}
                  </p>
                ) : null}
              </div>
            </div>
            <SettlementDetailTitleMenu
              ariaLabel={t('settlement.detailTitleMenuAria')}
              items={titleMenuItems}
            />
          </div>
        </div>
      </header>

      <main className={`mx-auto max-w-lg px-4 py-6 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : data && preview ? (
          <div className="space-y-6">
            {isFromPreviousHelper ? <PreviousHelperBanner variant="settlement" /> : null}
            {isEmployer && !isFromPreviousHelper ? (
              <div
                className="flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/90 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30"
                role="note"
                aria-live="polite"
              >
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700 dark:text-emerald-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M10.29 3.86l-8 14A1 1 0 003.16 19h17.68a1 1 0 00.87-1.5l-8-14a1 1 0 00-1.74 0z"
                  />
                </svg>
                <p className="text-sm leading-relaxed text-emerald-900 dark:text-emerald-100/90">
                  {t('settlement.unsettledEmployerSavedReceiptNotice')}
                </p>
              </div>
            ) : null}
            {showHelperResubmitBanner && data ? (
              <HelperResubmitSettlementBanner mainBudgetId={data.budget.id} />
            ) : null}
            <div className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20">
              <div className="flex items-center justify-end">
                <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  {t('settlement.status.unsettled')}
                </span>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('settlement.totalSpent')}</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatEmployerMoneyAmount(preview.totalSpent)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <span>{isHelper ? t('settlement.helperStatPeriodBudget') : t('settlement.budgetAmount')}</span>
                    {isHelper && preview.moneyReceivedDetails.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setMoneyReceivedDetailModalOpen(true)}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
                        aria-label={t('settlement.moneyReceivedDetailOpen')}
                        aria-haspopup="dialog"
                        aria-expanded={moneyReceivedDetailModalOpen}
                      >
                        <span aria-hidden>i</span>
                      </button>
                    ) : null}
                    {!readOnlyView ? (
                      <button
                        type="button"
                        onClick={handleOpenMoneyGivenEdit}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs text-primary-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:text-primary-300 dark:hover:bg-gray-800"
                        aria-label={t('budget.editAria', { start: data.budget.startDate, end: data.budget.endDate })}
                        aria-haspopup={hasSubMoneyGiven ? 'dialog' : undefined}
                        aria-expanded={hasSubMoneyGiven ? moneyGivenPickerOpen : undefined}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                          />
                        </svg>
                      </button>
                    ) : null}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatEmployerMoneyAmount(preview.budgetAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('settlement.balance')}</span>
                  <span className={`font-semibold ${balanceClass}`}>
                    {(preview.balance >= 0 ? t('settlement.surplus') : t('settlement.overspent'))}{' '}
                    {formatEmployerMoneyAmount(Math.abs(preview.balance))}
                  </span>
                </div>
              </div>
            </div>

            <section aria-labelledby="unsettled-receipts-heading">
              <h2 id="unsettled-receipts-heading" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('settlement.receiptsTitle')}
              </h2>
              {preview.receipts.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('settlement.empty')}</p>
              ) : (
                <ul className="mt-2 space-y-2" role="list">
                  {preview.receipts.map((receipt) => (
                    <li
                      key={receipt.id}
                      className="rounded-lg bg-white p-3 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-gray-600 dark:text-gray-300">{receipt.receiptDate}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {formatEmployerMoneyAmount(receipt.totalAmount)}
                            </span>
                          </div>
                          {receipt.company ? (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{receipt.company}</p>
                          ) : null}
                        </div>
                        <Link
                          to={`/receipts/${receipt.id}`}
                          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                          tabIndex={0}
                          aria-label={t('settlement.viewReceipt')}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <SettlementHistorySection
              title={t('settlement.pastSettlementHistoryTitle')}
              items={data.settlementHistory}
              submittedAtLabel={t('settlement.submittedAtLabel')}
              eventAtLabel={t('settlement.historyEventAtLabel')}
              moneyGivenAmountLabel={
                isHelper ? t('settlement.helperStatPeriodBudget') : t('settlement.budgetAmount')
              }
              totalSpentLabel={t('settlement.totalSpent')}
              settlementResultLabel={t('settlement.balance')}
              rejectReasonLabel={t('task.today.employerFeedbackLabel')}
              receiptsTitle={t('settlement.receiptsTitle')}
              viewDetailAria={t('settlement.historyViewDetailAria')}
              closeLabel={t('common.close')}
            />
          </div>
        ) : null}
      </main>

      {moneyGivenDetailModalOpen && data && preview && isEmployer ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyGivenDetailModalOpen(false)}
          settlementStart={data.budget.startDate}
          settlementEnd={data.budget.endDate}
          rows={preview.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyGivenDetailTitle"
          titleId="unsettled-settlement-money-given-title"
        />
      ) : null}

      {moneyReceivedDetailModalOpen && data && preview && isHelper ? (
        <MoneyReceivedDetailModal
          open
          onClose={() => setMoneyReceivedDetailModalOpen(false)}
          settlementStart={data.budget.startDate}
          settlementEnd={data.budget.endDate}
          rows={preview.moneyReceivedDetails}
          t={t}
          titleKey="settlement.moneyReceivedDetailTitle"
          titleId="unsettled-settlement-money-received-title"
        />
      ) : null}

      {moneyGivenPickerOpen && data && isEmployer ? (
        <AppModal
          open
          onClose={() => setMoneyGivenPickerOpen(false)}
          title={t('settlement.budgetAmount')}
          titleId="money-given-edit-picker-title"
          closeAriaLabel={t('common.close')}
        >
          <ul className="flex flex-col gap-3" role="list">
            <li className="rounded-xl border border-orange-200/50 bg-orange-50/50 p-3 shadow-sm dark:border-orange-900/40 dark:bg-orange-950/20">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-bold text-gray-900 dark:text-gray-100">
                  {t('settlement.period', { start: data.budget.startDate, end: data.budget.endDate })}
                </span>
                <Link
                  to={`/budgets/${data.budget.id}/edit`}
                  state={{ from: currentPath }}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                  aria-label={t('budget.editAria', { start: data.budget.startDate, end: data.budget.endDate })}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </Link>
              </div>
              <div className="mt-2 flex items-center justify-between pl-1">
                <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{t('budget.mainAmountLabel')}</span>
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {formatEmployerMoneyAmount(mainMoneyGivenAmount)}
                </span>
              </div>
            </li>
            {subMoneyGivenRows.map((row) => (
              <li
                key={row.id}
                className="rounded-xl border border-emerald-200/50 bg-emerald-50/40 p-3 shadow-sm dark:border-emerald-900/40 dark:bg-emerald-950/20"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-bold text-gray-900 dark:text-gray-100">
                    {t('settlement.period', { start: row.startDate, end: row.endDate })}
                  </span>
                  <Link
                    to={`/budgets/${row.id}/edit`}
                    state={{ from: currentPath }}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-primary-600 shadow-sm hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                    aria-label={t('budget.editAria', { start: row.startDate, end: row.endDate })}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                      />
                    </svg>
                  </Link>
                </div>
                <div className="mt-2 flex items-center justify-between pl-1">
                  <span className="text-[13px] font-medium text-gray-500 dark:text-gray-400">{t('budget.subAmountLabel')}</span>
                  <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {formatEmployerMoneyAmount(row.amount)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </AppModal>
      ) : null}
    </div>
  );
};
